/**
 * The PROPOSED SEASON, day by day (Chris, 2026-08-21): a month-calendar grid of
 * every designed month — each running day painted with the NEW timetable
 * pattern the season design banked for it, with its workings count. Identical
 * designed timetables are folded into named patterns (Plan A, Plan B, …) shown
 * in a legend. Clicking a day opens its drill-down: the workings it runs, the
 * money against the unoptimised (current) day, the modelled ticket split, and
 * the full cost line items on BOTH sides — every number inspectable, nothing
 * asserted (law 8).
 */
import { useEffect, useRef, useState } from 'react'
import type { CostLine } from './costs'
import type { Departure } from './types'

const DOW_HDR = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const num0 = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 })
const gbp0 = (n: number) => `£${Math.round(n).toLocaleString('en-GB')}`
const fmtMin = (mins: number) => `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(Math.round(mins) % 60).padStart(2, '0')}`

/** Distinct colours for the NEW patterns — deliberately not the railway's
 *  Yellow/Red/Pink/Blue, so a proposed pattern can never be misread as a
 *  current one. */
export const PLAN_COLOURS = ['#2C7FA6', '#7B5EA7', '#C96F1F', '#3E8E5A', '#B23A48', '#6B7F3A', '#20808D', '#8A5A83', '#4E6A8A', '#A5652E']

export interface SeasonPlanDay {
  date: string
  dayNum: number
  /** ISO day of week, Mon=1 … Sun=7. */
  dow: number
  /** The banked pattern this day runs, null when the day has no design
   *  (gala/diner or an unscorable group). */
  patternId: string | null
  services: number | null
  /** e.g. 'Gala' — running day with no standard pattern to redesign. */
  note?: string
  /** Key to fetch the drill-down: `${mk}|${COLOUR}|${dayClass}`. */
  groupKey?: string
  /** A Firecon day (firecon.ts): steam stood down, diesel only; which day of the run and the sales % down. */
  firecon?: { day: number; pct: number }
  /** A FAILURE DAY (engineRoster.ts): the turns no engine of any traction could take — red, non-running. */
  failure?: string[]
}

export interface SeasonPlanMonth {
  mk: string
  label: string
  weeks: (SeasonPlanDay | null)[][]
}

export interface SeasonPlanPattern {
  id: string
  name: string
  colour: string
  workings: number
  daysUsed: number
  monthsUsed: string[]
  /** One group that runs this pattern — the drill-down target for the legend. */
  sampleKey: string
  sampleDate: string
}

export interface SeasonPlanData {
  months: SeasonPlanMonth[]
  patterns: SeasonPlanPattern[]
}

export interface PlanWorkingRow {
  time: string
  from: string
  to: string
  coaches: number
  capacity: number | null
  positioning: boolean
  traction?: string
  set?: string
}

export interface PlanSideDetail {
  services: number
  steamN: number
  dieselN: number
  sets: number
  carried: number
  revenue: number
  costs: number
  profit: number
  workings: PlanWorkingRow[]
  costLines: CostLine[]
}

export interface SeasonDayDetail {
  basis: string
  /** The day-type of the DATE the planner clicked — what these figures value. */
  dayClass: string
  /** The whole week this one timetable covers, e.g. "Saturday x5 - Weekday x9". */
  mix?: string
  colour: string
  dates: string[]
  opt: PlanSideDetail
  cur: PlanSideDetail | null
  /** The banked plan's raw departures — the editor's working copy source. */
  editDeps: Departure[]
  stations: string[]
}

export interface TicketSplitRow { item: string; tickets: number; net: number; share: number }

export interface SeasonPlanPanelProps {
  plan: SeasonPlanData
  /** Compute a group's drill-down on demand (~100ms of engine work). The DATE
   *  decides which day-type's sales the figures are valued on (law 6). */
  detailFor: (groupKey: string, date?: string) => SeasonDayDetail | null
  /** The month's observed ticket mix scaled to the day's modelled pax/revenue. */
  splitFor: (groupKey: string, pax: number, revenue: number, date?: string) => Promise<TicketSplitRow[] | null>
  /** Carriage set codes offered by the editor's Set picker. */
  setCodes: string[]
  /** Apply an edit: scope 'day' = just the clicked date, 'set' = every day
   *  this timetable covers in the month. Re-scores and re-banks the month. */
  applyEdit: (groupKey: string, date: string, scope: 'day' | 'set', deps: Departure[]) => Promise<boolean>
}

/** Applied departures → display rows for the drill-down workings list. */
export function depsToPlanRows(deps: Departure[], stations: string[]): PlanWorkingRow[] {
  return deps
    .filter((d) => d.enabled && Number.isFinite(d.minutes))
    .sort((a, b) => a.minutes - b.minutes)
    .map((d) => ({
      time: fmtMin(d.minutes),
      from: stations[d.o] ?? String(d.o),
      to: stations[d.d] ?? String(d.d),
      coaches: d.coaches,
      capacity: d.capacity ?? null,
      positioning: !!d.positioning,
      traction: d.tractionPin,
      set: d.setPin,
    }))
}

function SideMoney({ label, s, other, onWorkings, onCosts, onRevenue }: {
  label: string; s: PlanSideDetail; other: PlanSideDetail | null
  onWorkings: () => void; onCosts: () => void; onRevenue?: () => void
}) {
  const d = (v: number, o: number | undefined) => o == null ? null : v - o
  const profitD = d(s.profit, other?.profit)
  return (
    <div className="sh-card" style={{ flex: 1, minWidth: 240 }}>
      <div className="sh-card-head"><span className="sh-card-title">{label}</span></div>
      <table className="stu-table">
        <tbody>
          <tr><td>Services</td><td className="num sh-mono"><button type="button" className="sh-linklike" onClick={onWorkings}>{s.services}</button></td></tr>
          <tr><td>Locos</td><td className="num sh-mono">{s.steamN}S{s.dieselN > 0 ? `+${s.dieselN}D` : ''}</td></tr>
          <tr><td>Sets</td><td className="num sh-mono">{s.sets}</td></tr>
          <tr><td>Passengers</td><td className="num sh-mono">{num0.format(Math.round(s.carried))}</td></tr>
          <tr><td>Revenue</td><td className="num sh-mono">{onRevenue ? <button type="button" className="sh-linklike" onClick={onRevenue}>{gbp0(s.revenue)}</button> : gbp0(s.revenue)}</td></tr>
          <tr><td>Costs</td><td className="num sh-mono"><button type="button" className="sh-linklike" onClick={onCosts}>{gbp0(s.costs)}</button></td></tr>
          <tr><td><strong>Profit</strong></td><td className={`num sh-mono ${s.profit >= 0 ? 'sh-delta--good' : 'sh-delta--bad'}`}><strong>{s.profit >= 0 ? '+' : ''}{gbp0(s.profit)}</strong>{profitD != null && <span className="sh-dim"> ({profitD >= 0 ? '+' : ''}{gbp0(profitD)})</span>}</td></tr>
        </tbody>
      </table>
    </div>
  )
}

export function SeasonPlanPanel({ plan, detailFor, splitFor, setCodes, applyEdit }: SeasonPlanPanelProps) {
  const [selKey, setSelKey] = useState<string | null>(null)
  const [selDate, setSelDate] = useState<string | null>(null)
  const [detail, setDetail] = useState<SeasonDayDetail | null>(null)
  const [drill, setDrill] = useState<'none' | 'workings' | 'costs' | 'split'>('none')
  const [drillSide, setDrillSide] = useState<'opt' | 'cur'>('opt')
  const [split, setSplit] = useState<TicketSplitRow[] | null | 'loading'>(null)
  // The editor (Chris, 2026-08-21): edit a banked timetable's workings and
  // choose INLINE whether it applies to just this day or the whole set.
  const [draft, setDraft] = useState<Departure[] | null>(null)
  const [scopeAsk, setScopeAsk] = useState(false)
  const [editBusy, setEditBusy] = useState(false)
  const [editMsg, setEditMsg] = useState<string | null>(null)
  // Props go stale across an await (the orchestrator reloads the slices) —
  // always call the LATEST detailFor/applyEdit.
  const detailForRef = useRef(detailFor); detailForRef.current = detailFor
  const applyEditRef = useRef(applyEdit); applyEditRef.current = applyEdit
  // When the banked plans change (an edit re-scored the month), recompute the
  // open drill-down from the FRESH data — the immediate refresh after an
  // apply can race React's re-render and briefly show the old timetable.
  useEffect(() => {
    if (selKey) setDetail(detailForRef.current(selKey, selDate ?? undefined))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan])

  const patternById = new Map(plan.patterns.map((p) => [p.id, p]))
  const openDay = (groupKey: string, date: string) => {
    setDraft(null); setScopeAsk(false); setEditMsg(null)
    if (selKey === groupKey && selDate === date) { setSelKey(null); setSelDate(null); setDetail(null); setDrill('none'); return }
    setSelKey(groupKey); setSelDate(date); setDrill('none'); setSplit(null)
    setDetail(detailFor(groupKey, date))
  }
  const commitEdit = async (scope: 'day' | 'set') => {
    if (!selKey || !selDate || !draft) return
    setEditBusy(true); setScopeAsk(false); setEditMsg(null)
    try {
      const ok = await applyEditRef.current(selKey, selDate, scope, draft)
      if (ok) {
        setDraft(null)
        setDetail(detailForRef.current(selKey, selDate ?? undefined))
        setEditMsg('Applied — the month has been re-scored and saved; every table above reflects it.')
      } else {
        setEditMsg('Could not apply the change — the session must be saved and the month designed first.')
      }
    } finally {
      setEditBusy(false)
    }
  }
  const openSplit = (pax: number, revenue: number) => {
    if (!selKey) return
    setDrill('split'); setSplit('loading')
    void splitFor(selKey, pax, revenue, selDate ?? undefined).then((rows) => setSplit(rows))
  }

  if (plan.months.length === 0) return null
  return (
    <section className="sh-card sh-mt">
      <div className="sh-card-head">
        <span className="sh-card-icon">🗓</span>
        <span className="sh-card-title">The proposed season — every day, its new timetable</span>
        <span className="sh-card-note">click a day for its workings, ticket split and cost line items · click a pattern for where it runs</span>
      </div>

      {/* Legend: the NEW timetable patterns the design created. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '8px 12px 0' }}>
        {plan.patterns.map((p) => (
          <button key={p.id} type="button" className="sh-plan-chip" style={{ borderColor: p.colour }} onClick={() => openDay(p.sampleKey, p.sampleDate)}>
            <span className="sh-plan-swatch" style={{ background: p.colour }} />
            <strong>{p.name}</strong>
            <span className="sh-dim"> · {p.workings} working{p.workings === 1 ? '' : 's'}/day · {p.daysUsed} day{p.daysUsed === 1 ? '' : 's'} · {p.monthsUsed.join(', ')}</span>
          </button>
        ))}
      </div>

      {/* Month grids. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12, padding: 12 }}>
        {plan.months.map((m) => (
          <div key={m.mk} className="sh-plan-month">
            <div className="sh-plan-month-title">{m.label}</div>
            <div className="sh-plan-grid">
              {DOW_HDR.map((h) => <div key={h} className="sh-plan-dowhdr">{h}</div>)}
              {m.weeks.flatMap((row, wi) => row.map((day, di) => {
                if (!day) return <div key={`${wi}-${di}`} className="sh-plan-cell sh-plan-cell--off" />
                const p = day.patternId ? patternById.get(day.patternId) : null
                const selected = selDate === day.date
                return (
                  <button
                    key={day.date} type="button"
                    className={`sh-plan-cell${selected ? ' sh-plan-cell--sel' : ''}${!p && !day.note ? ' sh-plan-cell--norun' : ''}${day.failure ? ' sh-plan-cell--fail' : ''}`}
                    style={p && !day.failure ? { background: p.colour } : undefined}
                    title={`${p ? `${day.date} · ${p.name} · ${day.services} workings` : day.note ? `${day.date} · ${day.note}` : `${day.date} — not designed`}${day.firecon ? ` · Firecon day ${day.firecon.day} — steam stood down, diesel only${day.firecon.pct > 0 ? `, sales −${day.firecon.pct}%` : ''}` : ''}${day.failure ? ` · FAILURE DAY — no engine of any traction for: ${day.failure.map((k) => k.split('|').slice(1).join(' ')).join(', ')} — modelled as a non-running day; tick another engine, move an exam, or take a working out` : ''}`}
                    onClick={() => day.groupKey && openDay(day.groupKey, day.date)}
                    disabled={!day.groupKey}
                  >
                    <span className="sh-plan-daynum">{day.dayNum}</span>
                    {day.firecon && <span className="sh-plan-firecon" aria-label="Firecon day">F</span>}
                    <span className="sh-plan-svc">{day.failure ? '✕' : p ? `${day.services}` : day.note?.startsWith('no working') ? '∅' : day.note ? '★' : '—'}</span>
                  </button>
                )
              }))}
            </div>
          </div>
        ))}
      </div>

      {/* Day drill-down. */}
      {selKey && detail && (
        <div style={{ padding: '0 12px 12px' }}>
          <div className="sh-card-head" style={{ paddingLeft: 0 }}>
            <span className="sh-card-title">
              {selDate} · {detail.dayClass} · was {detail.colour} timetable
              {detail.mix ? <span className="stu-note"> · one {detail.colour} timetable across {detail.mix}</span> : null}
            </span>
            <span className="sh-card-note">same design banks to {detail.dates.length} day{detail.dates.length === 1 ? '' : 's'} · demand basis: {detail.basis}</span>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <SideMoney label="Proposed (optimised)" s={detail.opt} other={detail.cur}
              onWorkings={() => { setDrill('workings'); setDrillSide('opt') }}
              onCosts={() => { setDrill('costs'); setDrillSide('opt') }}
              onRevenue={() => openSplit(detail.opt.carried, detail.opt.revenue)} />
            {detail.cur && (
              <SideMoney label="Current (unoptimised)" s={detail.cur} other={detail.opt}
                onWorkings={() => { setDrill('workings'); setDrillSide('cur') }}
                onCosts={() => { setDrill('costs'); setDrillSide('cur') }} />
            )}
          </div>

          {/* The editor — loco, times, carriage set, remove/restore. */}
          <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {!draft && (
              <button type="button" className="sh-plan-chip" onClick={() => { setDraft(detail.editDeps.map((x) => ({ ...x }))); setDrill('none'); setEditMsg(null) }}>
                ✎ Edit this timetable
              </button>
            )}
            {editMsg && <span className="sh-card-note">{editMsg}</span>}
          </div>
          {draft && (
            <div className="sh-card sh-mt">
              <div className="sh-card-head">
                <span className="sh-card-title">Editing — {detail.colour} {detail.dayClass}</span>
                <span className="sh-card-note">change times, traction or set; Remove takes a working out. Nothing is saved until you apply.</span>
              </div>
              <div className="stu-scroll">
                <table className="stu-table">
                  <thead><tr><th>Dep</th><th>Journey</th><th>Traction</th><th>Set</th><th /></tr></thead>
                  <tbody>
                    {draft.filter((x) => Number.isFinite(x.minutes)).sort((a, b) => a.minutes - b.minutes).map((w) => {
                      const upd = (patch: Partial<Departure>) => setDraft((prev) => prev!.map((x) => (x.id === w.id ? { ...x, ...patch } : x)))
                      const hh = String(Math.floor(w.minutes / 60)).padStart(2, '0')
                      const mm2 = String(Math.round(w.minutes) % 60).padStart(2, '0')
                      return (
                        <tr key={w.id} style={w.enabled ? undefined : { opacity: 0.45 }}>
                          <td>
                            <input type="time" value={`${hh}:${mm2}`} disabled={!w.enabled}
                              onChange={(e) => {
                                const [h, m] = e.target.value.split(':').map(Number)
                                if (!Number.isFinite(h)) return
                                const nm = h * 60 + m
                                upd({ minutes: nm, time: e.target.value, arrMin: w.arrMin != null ? w.arrMin + (nm - w.minutes) : undefined })
                              }} />
                          </td>
                          <td>{detail.stations[w.o] ?? w.o} to {detail.stations[w.d] ?? w.d}{w.positioning ? ' (ECS)' : ''}</td>
                          <td>
                            <select value={w.tractionPin ?? 'auto'} disabled={!w.enabled}
                              onChange={(e) => upd({ tractionPin: e.target.value === 'auto' ? undefined : e.target.value as Departure['tractionPin'] })}>
                              <option value="auto">Auto</option>
                              <option value="steam">Steam</option>
                              <option value="diesel">Diesel</option>
                              <option value="railcar">Railcar</option>
                            </select>
                          </td>
                          <td>
                            <select value={w.setPin ?? 'auto'} disabled={!w.enabled}
                              onChange={(e) => upd({ setPin: e.target.value === 'auto' ? undefined : e.target.value })}>
                              <option value="auto">Auto</option>
                              {setCodes.map((c2) => <option key={c2} value={c2}>{c2}</option>)}
                            </select>
                          </td>
                          <td>
                            <button type="button" className="sh-linklike" onClick={() => upd({ enabled: !w.enabled })}>
                              {w.enabled ? 'Remove' : 'Restore'}
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <div className="sh-card-foot" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {!scopeAsk && (
                  <>
                    <button type="button" className="sh-plan-chip" disabled={editBusy} onClick={() => setScopeAsk(true)}><strong>Apply changes…</strong></button>
                    <button type="button" className="sh-linklike" disabled={editBusy} onClick={() => { setDraft(null); setScopeAsk(false) }}>Discard</button>
                  </>
                )}
                {scopeAsk && !editBusy && (
                  <>
                    <span><strong>Apply to:</strong></span>
                    <button type="button" className="sh-plan-chip" onClick={() => void commitEdit('day')}>Just {selDate}</button>
                    <button type="button" className="sh-plan-chip" onClick={() => void commitEdit('set')}>
                      All {detail.dates.length} day{detail.dates.length === 1 ? '' : 's'} of this timetable
                    </button>
                    <button type="button" className="sh-linklike" onClick={() => setScopeAsk(false)}>Back</button>
                  </>
                )}
                {editBusy && <span className="sh-card-note">Re-scoring the month and saving…</span>}
              </div>
            </div>
          )}

          {drill === 'workings' && (() => {
            const side = drillSide === 'opt' ? detail.opt : detail.cur
            if (!side) return null
            return (
              <div className="sh-card sh-mt">
                <div className="sh-card-head"><span className="sh-card-title">Workings — {drillSide === 'opt' ? 'proposed' : 'current'} timetable</span></div>
                <div className="stu-scroll">
                  <table className="stu-table">
                    <thead><tr><th>Dep</th><th>From → To</th><th className="num">Coaches</th><th className="num">Seats</th><th>Traction</th><th>Set</th><th /></tr></thead>
                    <tbody>
                      {side.workings.map((w, i) => (
                        <tr key={i}>
                          <td className="sh-mono">{w.time}</td>
                          <td>{w.from} → {w.to}</td>
                          <td className="num sh-mono">{w.coaches}</td>
                          <td className="num sh-mono">{w.capacity ?? '—'}</td>
                          <td>{w.traction ?? 'auto'}</td>
                          <td>{w.set ?? 'auto'}</td>
                          <td>{w.positioning ? <span className="sh-tag">ECS — runs empty</span> : null}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          })()}

          {drill === 'costs' && (
            <div className="sh-card sh-mt">
              <div className="sh-card-head">
                <span className="sh-card-title">Costs by line item — proposed vs current</span>
                <span className="sh-card-note">both sides priced with today's cost model; lines sum exactly to the day totals</span>
              </div>
              <div className="stu-scroll">
                <table className="stu-table">
                  <thead><tr><th>Line</th><th className="num">Proposed</th><th className="num">Current</th><th className="num">Δ</th><th>Basis</th></tr></thead>
                  <tbody>
                    {detail.opt.costLines.map((l, i) => {
                      const c = detail.cur?.costLines[i]
                      const dd = c ? l.amount - c.amount : null
                      return (
                        <tr key={l.label}>
                          <td>{l.label}</td>
                          <td className="num sh-mono">{gbp0(l.amount)}</td>
                          <td className="num sh-mono">{c ? gbp0(c.amount) : '—'}</td>
                          <td className={`num sh-mono ${dd == null ? '' : dd <= 0 ? 'sh-delta--good' : 'sh-delta--bad'}`}>{dd == null ? '—' : `${dd >= 0 ? '+' : '−'}${gbp0(Math.abs(dd))}`}</td>
                          <td className="sh-dim" style={{ fontSize: '0.72rem' }}>{l.detail}</td>
                        </tr>
                      )
                    })}
                    <tr>
                      <td><strong>Total</strong></td>
                      <td className="num sh-mono"><strong>{gbp0(detail.opt.costs)}</strong></td>
                      <td className="num sh-mono"><strong>{detail.cur ? gbp0(detail.cur.costs) : '—'}</strong></td>
                      <td className={`num sh-mono ${detail.cur && detail.opt.costs - detail.cur.costs <= 0 ? 'sh-delta--good' : 'sh-delta--bad'}`}>
                        <strong>{detail.cur ? `${detail.opt.costs - detail.cur.costs >= 0 ? '+' : '−'}${gbp0(Math.abs(detail.opt.costs - detail.cur.costs))}` : '—'}</strong>
                      </td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {drill === 'split' && (
            <div className="sh-card sh-mt">
              <div className="sh-card-head">
                <span className="sh-card-title">Proposed ticket split — this day's modelled revenue over the observed ticket mix</span>
              </div>
              {split === 'loading' && <div className="sh-dim" style={{ padding: 12 }}>Loading the month's observed ticket mix…</div>}
              {split !== 'loading' && !split && <div className="sh-dim" style={{ padding: 12 }}>No observed ticket mix for this month yet.</div>}
              {Array.isArray(split) && (
                <div className="stu-scroll">
                  <table className="stu-table">
                    <thead><tr><th>Ticket</th><th className="num">Tickets</th><th className="num">Net £</th><th className="num">Share</th></tr></thead>
                    <tbody>
                      {split.map((r) => (
                        <tr key={r.item}>
                          <td>{r.item}</td>
                          <td className="num sh-mono">{num0.format(Math.round(r.tickets))}</td>
                          <td className="num sh-mono">{gbp0(r.net)}</td>
                          <td className="num sh-mono">{Math.round(r.share * 100)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="sh-card-foot">
        Colours here are the PROPOSED patterns (never the railway's current Yellow/Red/Pink). ★ = a running day with no
        standard pattern to redesign (gala/diner) — it keeps its special programme. ∅ = the design found no working that
        pays on this day (an empty timetable is not an answer — click it to see the current side and why). A day's
        drill-down compares the banked design against the real current timetable for that day's colour, both priced with
        today's cost model.
      </div>
    </section>
  )
}
